//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
//Third party libraries
import { useParams } from 'react-router';
import ProjectUsesHookFieldArray, { projectUsesValidationSchema } from '@/components/Sherpa/Projects/ProjectUsesHookFieldArray';
//Custom components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { notifyUser } from '../../../../../../../store/Toast';
//Assets andd Miscellaneous
import type { ProjectUseInterface } from '../../../../../../../types/Project';
//Services
import { formatNumber, projectBudgetTotal, sanitizeResponseError } from '../../../../../../../utils';

interface IUpdateProjectUsesProps {
  handleModal: () => void;
}

export default function UpdateProjectUses({ handleModal }: IUpdateProjectUsesProps) {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const uses = fetchProject?.data?.data?.uses;
  const sources = fetchProject?.data?.data?.sources;

  const updateProjectUses = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project uses update successful',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: { uses: ProjectUseInterface[] }) => {
    const usesTotal = projectBudgetTotal(formData.uses);
    const sourcesTotal = projectBudgetTotal(sources || []);
    if (usesTotal > sourcesTotal) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Uses total must be less or equal to total sources amount of $${formatNumber(sourcesTotal)}`,
          toastType: 'failed',
        })
      );
    } else {
      updateProjectUses.mutate(formData);
    }
  };

  return (
    <Modal>
      <HookFormProvider defaultValues={{ uses: uses || [] }} resolver={yupResolver(projectUsesValidationSchema) as any} mode='all'>
        <HookForm onSubmit={onSubmit}>
          <h2 className='mt-0'>Project Uses</h2>
          <ProjectUsesHookFieldArray sources={sources || []} />
          <div className='flex justify-end gap-4 mt-5'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
            <Button label={`${updateProjectUses.isLoading ? 'Saving...' : 'Save'}`} btnStyle='btn-primary' type='submit' />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
}
